import { gql } from '@apollo/client';

/**
 * The Reports page's reports. Every query takes the same `range`
 * (ReportRangeInput): dateFrom, dateTo (inclusive), timezone, granularity and
 * the three region filters. What each number means is in the backend's
 * docs/DASHBOARD_REPORTS.md.
 */

const MEMBER = `
  id
  firstName
  lastName
  username
  isDeleted
`;

export const REPORT_ENGAGEMENT = gql`
  query reportEngagement($range: ReportRangeInput!) {
    reportEngagement(range: $range) {
      totals {
        activeMembers
        newMembers
        returningMembers
        sessions
        engagementSeconds
        averageSessionSeconds
        averageEngagementSecondsPerMember
        averageSessionsPerMember
        screenViews
      }
      events {
        type
        count
      }
      periods {
        period
        activeMembers
        newMembers
        returningMembers
        sessions
        engagementSeconds
      }
    }
  }
`;

const RETENTION_DAY = `eligible retained rate`;

export const REPORT_RETENTION = gql`
  query reportRetention($range: ReportRangeInput!) {
    reportRetention(range: $range) {
      overall {
        cohort
        members
        day1 { ${RETENTION_DAY} }
        day7 { ${RETENTION_DAY} }
        day30 { ${RETENTION_DAY} }
      }
      cohorts {
        cohort
        members
        day1 { ${RETENTION_DAY} }
        day7 { ${RETENTION_DAY} }
        day30 { ${RETENTION_DAY} }
      }
    }
  }
`;

export const REPORT_EVENTS = gql`
  query reportEvents($range: ReportRangeInput!, $filter: ReportEventFilterInput, $page: ReportPageInput) {
    reportEvents(range: $range, filter: $filter, page: $page) {
      total
      counts {
        type
        count
      }
      events {
        id
        type
        occurredAt
        detail
        member { ${MEMBER} }
        relatedMember { ${MEMBER} }
        business {
          id
          name
          address
        }
      }
    }
  }
`;

export const REPORT_SCREENS = gql`
  query reportScreens($range: ReportRangeInput!, $userId: Int) {
    reportScreens(range: $range, userId: $userId) {
      totalViews
      totalMembers
      screens {
        screen
        label
        views
        members
      }
    }
  }
`;

export const REPORT_SCREEN_VIEWS = gql`
  query reportScreenViews($range: ReportRangeInput!, $filter: ReportScreenFilterInput, $page: ReportPageInput) {
    reportScreenViews(range: $range, filter: $filter, page: $page) {
      total
      views {
        id
        screen
        label
        raw
        viewedAt
        member { ${MEMBER} }
      }
    }
  }
`;

/** Member search for the "one member" filters; the reports filter by id. */
export const REPORT_MEMBER_SEARCH = gql`
  query reportMemberSearch($filter: String!) {
    searchUsers(query: $filter) {
      id
      username
      firstName
      lastName
    }
  }
`;

/** The daily report: a row per metric, a value per day (at most 31). */
export const REPORT_DAILY = gql`
  query reportDaily($range: ReportRangeInput!) {
    reportDaily(range: $range) {
      days
      levelsRecordedSince
      chatMessagesSource
      phoneVerifiedDatesExactSince
      notes {
        section
        text
      }
      rows {
        metric
        section
        label
        hint
        source
        target
        values
        total
        totalSoFar
      }
    }
  }
`;

/** A typed-in number for one day; `value` null clears it. MANUAL rows only. */
export const SET_DAILY_REPORT_VALUE = gql`
  mutation setDailyReportValue($day: String!, $metric: String!, $value: Int) {
    setDailyReportValue(day: $day, metric: $metric, value: $value)
  }
`;

/** A row's daily target; `value` null removes it. */
export const SET_DAILY_REPORT_TARGET = gql`
  mutation setDailyReportTarget($metric: String!, $value: Int) {
    setDailyReportTarget(metric: $metric, value: $value)
  }
`;

/** The note that opens a section of a day's report; empty `text` removes it. */
export const SET_DAILY_REPORT_NOTE = gql`
  mutation setDailyReportNote($day: String!, $section: DailyReportSection!, $text: String) {
    setDailyReportNote(day: $day, section: $section, text: $text)
  }
`;
